import { AlertTriangle, CalendarOff } from 'lucide-react'
import { pendingAbsenceStyle } from '../../lib/absence-style'
import { absenceTooltip } from '../../lib/absence-tooltip'
import type {
  Absence,
  AbsenceType,
  Break,
  BreakRuleTier,
  Meeting,
  Nonbusinessday,
  TimeEntry,
  WorkSchedule,
} from '../../lib/api'
import { dayKey, isSameDay } from '../../lib/calendar'
import {
  type DayMetrics,
  isFutureDay,
  metricsForDay,
  sumMetricsUntilToday,
} from '../../lib/calendar-overlays'
import { useIsMobile } from '../../lib/use-media-query'
import { cn, formatHM } from '../../lib/utils'

const ABSENCE_COLOR: Record<AbsenceType, string> = {
  vacation: '#3B82F6',
  sick: '#EF4444',
  training: '#10B981',
  other: '#A3978C',
  overtime_reduction: '#A3978C',
  maternity: '#A3978C',
  parental: '#A3978C',
}
const ABSENCE_LABEL: Record<AbsenceType, string> = {
  vacation: 'Urlaub',
  sick: 'Krank',
  training: 'Fortbild.',
  other: 'Frei',
  overtime_reduction: 'Abbau',
  maternity: 'MuSch.',
  parental: 'Elter.',
}

interface WeekFooterProps {
  days: Date[]
  entries: TimeEntry[]
  breaks: Break[]
  absences: Absence[]
  holidays: Map<string, Nonbusinessday>
  breakTiers: BreakRuleTier[]
  targetHours: readonly WorkSchedule[]
  /** Meetings ohne überlappenden Time-Entry zählen zur Ist-Seite. */
  meetings?: Meeting[]
  /** Mobile Wochenansicht: der über die Tages-Tabs gewählte Tag — am Phone
   *  zeigt der Footer nur diesen Tag + die Wochensumme statt 7 geclippter
   *  Spalten (438px Inhalt in ~347px overflow-hidden, Sa+So unsichtbar). */
  selectedDay?: Date
}

export function WeekFooter({
  days,
  entries,
  breaks,
  absences,
  holidays,
  breakTiers,
  targetHours,
  meetings = [],
  selectedDay,
}: WeekFooterProps) {
  const now = new Date()
  const isMobile = useIsMobile()

  // Phone: eigener kompakter Branch — nur der gewählte Tag + Wochensumme.
  // Das Desktop-Markup darunter bleibt unverändert (eigener JSX-Zweig).
  if (isMobile) {
    const day =
      (selectedDay ? days.find((d) => isSameDay(d, selectedDay)) : undefined) ??
      days.find((d) => isSameDay(d, now)) ??
      days[0]
    if (!day) return null
    const metricsByDay = days.map((d) =>
      metricsForDay(d, entries, breaks, breakTiers, holidays, absences, targetHours, meetings),
    )
    const m = metricsByDay[days.indexOf(day)]
    // Zukunfts-Cap: Woche Σ zählt nur Tage bis heute (Ticket „tagesaktuell“).
    const sums = sumMetricsUntilToday(days, metricsByDay)
    const weekWorked = sums.workedMinutes
    const weekTarget = sums.targetMinutes
    const weekActual = sums.totalActualMinutes
    const weekDiff = weekActual - weekTarget
    const weekNeutral = Math.abs(weekDiff) < 15
    const today = isSameDay(day, now)
    const breakViolated = m.breakDeficitMinutes > 0
    const stretchViolated = m.unbrokenStretchViolation
    const overtime = m.workedMinutes > 10 * 60 && !m.absence
    const hasBadges =
      !!m.absence ||
      !!m.pendingAbsence ||
      !!m.holiday ||
      overtime ||
      stretchViolated ||
      breakViolated ||
      m.entriesOnAbsence
    return (
      <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
        <div className="px-3 py-2 space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-2xs uppercase tracking-wider text-text-tertiary shrink-0">
                {day.toLocaleDateString('de-DE', {
                  weekday: 'short',
                  day: '2-digit',
                  month: '2-digit',
                })}
              </span>
              <span
                className={cn(
                  'font-mono tabular-nums text-base',
                  today ? 'text-accent' : 'text-text-primary',
                )}
              >
                {formatHM(m.workedMinutes)}
              </span>
              {m.holiday && (
                <CalendarOff
                  className="h-3.5 w-3.5 text-warning shrink-0"
                  aria-label={m.holiday.name}
                />
              )}
            </div>
            <SollIstLine metrics={m} future={isFutureDay(day, now)} />
          </div>
          <DayTimesLine metrics={m} day={day} />
          {hasBadges && (
            <div className="flex items-center gap-1 overflow-x-auto whitespace-nowrap text-2xs [scrollbar-width:none]">
              {m.absence && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5"
                  style={{
                    backgroundColor: `${ABSENCE_COLOR[m.absence.type]}26`,
                    color: ABSENCE_COLOR[m.absence.type],
                  }}
                  title={
                    absenceTooltip(m.absence) || (m.absence.notes ?? ABSENCE_LABEL[m.absence.type])
                  }
                >
                  {ABSENCE_LABEL[m.absence.type]}
                </span>
              )}
              {m.pendingAbsence && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded border border-dashed px-1.5 py-0.5"
                  style={{
                    borderColor: ABSENCE_COLOR[m.pendingAbsence.type],
                    color: ABSENCE_COLOR[m.pendingAbsence.type],
                    ...pendingAbsenceStyle(ABSENCE_COLOR[m.pendingAbsence.type]),
                  }}
                  title={absenceTooltip(m.pendingAbsence) || ABSENCE_LABEL[m.pendingAbsence.type]}
                >
                  {ABSENCE_LABEL[m.pendingAbsence.type]} · ausstehend
                </span>
              )}
              {m.holiday && (
                <span
                  className="shrink-0 rounded px-1.5 py-0.5 bg-warning/15 text-warning"
                  title={m.holiday.name}
                >
                  {m.holiday.halfDay ? '½ ' : ''}
                  {m.holiday.name}
                </span>
              )}
              {overtime && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 bg-danger/15 text-danger"
                  title={`Arbeitszeitgesetz §3: max. 10h/Tag — bereits ${formatHM(m.workedMinutes)}`}
                >
                  <AlertTriangle className="h-2.5 w-2.5" />
                  Über 10h
                </span>
              )}
              {stretchViolated && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 bg-danger/15 text-danger"
                  title={`ArbZG §4: max. 6h hintereinander ohne Pause — längste Spanne hier: ${formatHM(m.longestRunMinutes)}`}
                >
                  <AlertTriangle className="h-2.5 w-2.5" />
                  {formatHM(m.longestRunMinutes)} ohne Pause
                </span>
              )}
              {breakViolated && !stretchViolated && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 bg-warning/15 text-warning"
                  title={`Pflichtpause ${m.requiredBreakMinutes}m — bisher ${m.takenBreakMinutes}m`}
                >
                  <AlertTriangle className="h-2.5 w-2.5" />
                  {m.breakDeficitMinutes}m Pause fehlt
                </span>
              )}
              {m.entriesOnAbsence && (
                <span
                  className="inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 bg-danger/15 text-danger"
                  title="Es existieren Zeiteinträge an einem Abwesenheitstag — bitte prüfen."
                >
                  <AlertTriangle className="h-2.5 w-2.5" />
                  Einträge trotz Abwesenheit
                </span>
              )}
            </div>
          )}
          {days.length > 1 && (
            <div className="flex items-center justify-between gap-2 border-t border-border-subtle pt-1.5">
              <span className="text-2xs uppercase tracking-wider text-text-tertiary">Woche Σ</span>
              <div className="flex items-center gap-1.5 font-mono tabular-nums text-2xs">
                <span className="text-sm text-text-primary">{formatHM(weekWorked)}</span>
                {weekTarget > 0 && (
                  <>
                    <span className="text-text-tertiary">Soll {formatHM(weekTarget)}</span>
                    <span
                      className={cn(
                        'rounded px-1 py-0.5',
                        weekNeutral && 'bg-bg-overlay/60 text-text-tertiary',
                        !weekNeutral && weekDiff >= 0 && 'bg-success/15 text-success',
                        !weekNeutral && weekDiff < 0 && 'bg-danger/15 text-danger',
                      )}
                      title={`Ist ${formatHM(weekActual)} − Soll ${formatHM(weekTarget)}`}
                    >
                      {weekDiff >= 0 ? '+' : '−'}
                      {formatHM(Math.abs(weekDiff))}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // Wochensumme für die Desktop-Σ-Zelle (Ticket: „Wochensumme fehlt im
  // Kalender") — exakt die Mobile-Semantik (Z. 79-83): weekWorked enthält den
  // Volltags-Abwesenheits-Credit (workedMinutes := targetMinutes an solchen
  // Tagen), Δ rechnet gegen totalActualMinutes. metricsByDay einmal berechnen
  // und in den Tagesspalten wiederverwenden.
  const metricsByDay = days.map((d) =>
    metricsForDay(d, entries, breaks, breakTiers, holidays, absences, targetHours, meetings),
  )
  // Zukunfts-Cap: die Σ-Zelle zählt nur Tage bis heute (Ticket „tagesaktuell“).
  const sums = sumMetricsUntilToday(days, metricsByDay)
  const weekWorked = sums.workedMinutes
  const weekTarget = sums.targetMinutes
  const weekActual = sums.totalActualMinutes
  const weekDiff = weekActual - weekTarget
  const weekNeutral = Math.abs(weekDiff) < 15
  const showWeekSum = days.length > 1 // Tagesansicht (days=[cursor]) braucht keine Summe

  return (
    <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 overflow-hidden">
      <div
        className="grid"
        style={{ gridTemplateColumns: `${showWeekSum ? 96 : 48}px repeat(${days.length}, 1fr)` }}
      >
        <div className="px-3 py-2.5 border-r border-border-subtle">
          <span className="text-2xs uppercase tracking-wider text-text-tertiary">Σ</span>
          {showWeekSum && (
            <>
              <p className="font-mono tabular-nums text-base text-text-primary">
                {formatHM(weekWorked)}
              </p>
              {weekTarget > 0 && (
                <p className="flex flex-wrap items-center gap-1 text-2xs font-mono tabular-nums">
                  <span className="text-text-tertiary">Soll {formatHM(weekTarget)}</span>
                  <span
                    className={cn(
                      'rounded px-1 py-0.5',
                      weekNeutral && 'bg-bg-overlay/60 text-text-tertiary',
                      !weekNeutral && weekDiff >= 0 && 'bg-success/15 text-success',
                      !weekNeutral && weekDiff < 0 && 'bg-danger/15 text-danger',
                    )}
                    title={`Ist ${formatHM(weekActual)} − Soll ${formatHM(weekTarget)}`}
                  >
                    {weekDiff >= 0 ? '+' : '−'}
                    {formatHM(Math.abs(weekDiff))}
                  </span>
                </p>
              )}
            </>
          )}
        </div>
        {days.map((d, dayIndex) => {
          const today = isSameDay(d, now)
          const m = metricsByDay[dayIndex]
          const breakViolated = m.breakDeficitMinutes > 0
          const stretchViolated = m.unbrokenStretchViolation
          // ArbZG §3: max 10h/day work time. >8h needs ≥6 months avg of ≤8h
          // for compliance, but the 10h hard cap is the line we flag.
          const overtime = m.workedMinutes > 10 * 60 && !m.absence
          return (
            <div
              key={dayKey(d)}
              className={cn(
                'px-3 py-2.5 border-l border-border-subtle space-y-1',
                m.holiday && 'bg-warning/[0.04]',
                today && !m.holiday && !m.absence && 'bg-accent/[0.02]',
              )}
              style={
                m.absence
                  ? { backgroundColor: `${ABSENCE_COLOR[m.absence.type]}14` }
                  : m.pendingAbsence
                    ? pendingAbsenceStyle(ABSENCE_COLOR[m.pendingAbsence.type])
                    : undefined
              }
            >
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    'font-mono tabular-nums text-base',
                    today ? 'text-accent' : 'text-text-primary',
                  )}
                >
                  {formatHM(m.workedMinutes)}
                </span>
                {m.holiday && (
                  <CalendarOff className="h-3.5 w-3.5 text-warning" aria-label={m.holiday.name} />
                )}
              </div>
              <SollIstLine metrics={m} future={isFutureDay(d, now)} />
              <DayTimesLine metrics={m} day={d} />
              <div className="flex flex-wrap items-center gap-1 text-2xs">
                {m.absence &&
                  (() => {
                    const a = m.absence
                    const sameStart = dayKey(d) === a.fromDate.slice(0, 10)
                    const sameEnd = dayKey(d) === a.toDate.slice(0, 10)
                    const sameSingle = a.fromDate.slice(0, 10) === a.toDate.slice(0, 10)
                    const isHalf = sameSingle
                      ? !!a.firstDayHalf
                      : (sameStart && !!a.firstDayHalf) || (sameEnd && !!a.lastDayHalf)
                    return (
                      <span
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 truncate max-w-full"
                        style={{
                          backgroundColor: `${ABSENCE_COLOR[a.type]}26`,
                          color: ABSENCE_COLOR[a.type],
                        }}
                        title={absenceTooltip(a) || (a.notes ?? ABSENCE_LABEL[a.type])}
                      >
                        {isHalf && <span className="font-semibold">½</span>}
                        {ABSENCE_LABEL[a.type]}
                      </span>
                    )
                  })()}
                {m.pendingAbsence && (
                  <span
                    className="inline-flex items-center gap-1 rounded border border-dashed px-1.5 py-0.5 truncate max-w-full"
                    style={{
                      borderColor: ABSENCE_COLOR[m.pendingAbsence.type],
                      color: ABSENCE_COLOR[m.pendingAbsence.type],
                    }}
                    title={absenceTooltip(m.pendingAbsence) || ABSENCE_LABEL[m.pendingAbsence.type]}
                  >
                    {ABSENCE_LABEL[m.pendingAbsence.type]} · ausstehend
                  </span>
                )}
                {m.holiday && (
                  <span
                    className="rounded px-1.5 py-0.5 bg-warning/15 text-warning truncate max-w-full"
                    title={m.holiday.name}
                  >
                    {m.holiday.halfDay ? '½ ' : ''}
                    {m.holiday.name}
                  </span>
                )}
                {overtime && (
                  <span
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-danger/15 text-danger"
                    title={`Arbeitszeitgesetz §3: max. 10h/Tag — bereits ${formatHM(m.workedMinutes)}`}
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    Über 10h
                  </span>
                )}
                {stretchViolated && (
                  <span
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-danger/15 text-danger"
                    title={`ArbZG §4: max. 6h hintereinander ohne Pause — längste Spanne hier: ${formatHM(m.longestRunMinutes)}`}
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {formatHM(m.longestRunMinutes)} ohne Pause
                  </span>
                )}
                {breakViolated && !stretchViolated && (
                  <span
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-warning/15 text-warning"
                    title={`Pflichtpause ${m.requiredBreakMinutes}m — bisher ${m.takenBreakMinutes}m`}
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    {m.breakDeficitMinutes}m Pause fehlt
                  </span>
                )}
                {m.entriesOnAbsence && (
                  <span
                    className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-danger/15 text-danger"
                    title="Es existieren Zeiteinträge an einem Abwesenheitstag — bitte prüfen."
                  >
                    <AlertTriangle className="h-2.5 w-2.5" />
                    Einträge trotz Abwesenheit
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Stundenzettel-Zeile pro Tag: '07:32–16:45 · P 0:30' aus erstem Start und
 *  letztem Ende der Tages-Segmente — die Zeiten, die man sonst per Klick auf
 *  ersten/letzten Block ablesen musste. Entfällt an Tagen ohne Einträge und an
 *  Volltags-Abwesenheiten ohne Einträge. Laufender Eintrag → offenes Ende
 *  („läuft"), an Mitternacht geclipptes Ende → '24:00'. P = ArbZG-anrechenbare
 *  Pausensumme (Chunks ≥15min), identisch zur Pausen-Badge-Logik — bewusst
 *  NICHT Anwesenheitsspanne minus Arbeitszeit. */
function DayTimesLine({ metrics, day }: { metrics: DayMetrics; day: Date }) {
  const { firstStartMs, lastEndMs, hasRunningEntry, takenBreakMinutes } = metrics
  if (firstStartMs == null || lastEndMs == null) return null
  if (metrics.absence && !metrics.entriesOnAbsence) return null
  const fmt = (ms: number) =>
    new Date(ms).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
  const nextMidnight = new Date(day)
  nextMidnight.setHours(0, 0, 0, 0)
  nextMidnight.setDate(nextMidnight.getDate() + 1)
  const end = hasRunningEntry
    ? 'läuft'
    : lastEndMs === nextMidnight.getTime()
      ? '24:00'
      : fmt(lastEndMs)
  return (
    <div
      className="text-2xs text-text-tertiary font-mono tabular-nums"
      title={`Arbeitstag ${fmt(firstStartMs)}–${end}${takenBreakMinutes > 0 ? ` · Pause ${formatHM(takenBreakMinutes)} (anrechenbar, Blöcke ≥15min)` : ''}`}
    >
      {fmt(firstStartMs)}–{end}
      {takenBreakMinutes > 0 && ` · P ${formatHM(takenBreakMinutes)}`}
    </div>
  )
}

/** Mini-Zeile pro Tag im Footer: 'Soll 8:00 · Δ +0:30' bzw. nur '·' wenn
 *  kein Soll für den Tag existiert (Wochenende / Pre-Hire). */
function SollIstLine({ metrics, future = false }: { metrics: DayMetrics; future?: boolean }) {
  const { targetMinutes, totalActualMinutes } = metrics
  // Zukunftstage: Soll als Planungsinfo behalten, aber kein Δ-Badge — sonst
  // steht jeder kommende Werktag der Woche dauerhaft auf −8:00 (rot).
  if (future) {
    if (targetMinutes === 0) return null
    return (
      <div className="text-2xs text-text-tertiary font-mono tabular-nums">
        Soll {formatHM(targetMinutes)}
      </div>
    )
  }
  if (targetMinutes === 0) {
    if (totalActualMinutes === 0) return null
    return (
      <div className="text-2xs text-text-tertiary font-mono tabular-nums">
        Ist {formatHM(totalActualMinutes)}
      </div>
    )
  }
  const diff = totalActualMinutes - targetMinutes
  const neutral = Math.abs(diff) < 15
  const positive = diff >= 0
  return (
    <div className="flex items-center gap-1.5 text-2xs font-mono tabular-nums">
      <span className="text-text-tertiary">Soll {formatHM(targetMinutes)}</span>
      <span
        className={cn(
          'rounded px-1 py-0.5',
          neutral && 'bg-bg-overlay/60 text-text-tertiary',
          !neutral && positive && 'bg-success/15 text-success',
          !neutral && !positive && 'bg-danger/15 text-danger',
        )}
        title={`Ist ${formatHM(totalActualMinutes)} − Soll ${formatHM(targetMinutes)}`}
      >
        {positive ? '+' : '−'}
        {formatHM(Math.abs(diff))}
      </span>
    </div>
  )
}
